import * as React from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  DragOverEvent,
  useDroppable,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, ChevronDown, ChevronUp, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CollapsibleParameterRow, type ParameterInfo } from "@/components/model-management/detail/collapsible-parameter-row";
import type { AdminParameterOverride } from "@genieartinc/schemas";

interface SortableParameterProps {
  paramName: string;
  paramInfo: ParameterInfo;
  override?: AdminParameterOverride;
  group: "input" | "output" | "settings";
  isMainOutput: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  onChange: (override: AdminParameterOverride) => void;
  onMainOutputChange?: (isMain: boolean) => void;
}

function SortableParameter({
  paramName,
  paramInfo,
  override,
  group,
  isMainOutput,
  isExpanded,
  onToggle,
  onChange,
  onMainOutputChange,
}: SortableParameterProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: paramName,
    disabled: isExpanded,
    data: { group, paramName }  // Store group info for drop detection
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative">
      {/* Drag Handle */}
      {!isExpanded && (
        <div
          {...attributes}
          {...listeners}
          className="absolute left-0 top-0 bottom-0 w-6 flex items-center justify-center cursor-grab active:cursor-grabbing hover:bg-muted/50 rounded-l-lg z-10"
        >
          <GripVertical className="h-3.5 w-3.5 text-muted-foreground" />
        </div>
      )}

      {/* Parameter Row with left padding for drag handle */}
      <div className={!isExpanded ? "pl-6" : ""}>
        <CollapsibleParameterRow
          paramInfo={paramInfo}
          override={override}
          group={group}
          isMainOutput={isMainOutput}
          isExpanded={isExpanded}
          onToggle={onToggle}
          onChange={onChange}
          onMainOutputChange={onMainOutputChange}
        />
      </div>
    </div>
  );
}

interface DroppableGroupProps {
  id: string;
  title: string;
  color: string;
  count: number;
  mainOutputParam?: string | null;
  children: React.ReactNode;
  isCollapsed: boolean;
  onToggleAll: () => void;
  helpText: string;
  isActiveDropZone: boolean;
}

function DroppableGroup({
  id,
  title,
  color,
  count,
  mainOutputParam,
  children,
  isCollapsed,
  onToggleAll,
  helpText,
  isActiveDropZone,
}: DroppableGroupProps) {
  const { setNodeRef } = useDroppable({ id, data: { group: id } });

  return (
    <div
      ref={setNodeRef}
      className={`space-y-3 rounded-lg border-2 p-6 transition-all duration-300 ease-in-out ${
        isActiveDropZone
          ? "border-blue-300/60 dark:border-blue-500/50 bg-blue-50/40 dark:bg-blue-950/30 shadow-[0_0_0_4px_rgba(96,165,250,0.08)] dark:shadow-[0_0_0_4px_rgba(59,130,246,0.12)] scale-[1.005]"
          : "border-border bg-card"
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5">
            <div className={`h-2 w-2 rounded-full ${color}`} />
            <h3 className="text-sm font-semibold">{title}</h3>
          </div>
          <span className="text-xs text-muted-foreground">({count} parameters)</span>
          {mainOutputParam && id === "output" && (
            <div className="flex items-center gap-1 text-[10px] text-amber-600">
              <Star className="h-2.5 w-2.5 fill-current" />
              <span>{mainOutputParam}</span>
            </div>
          )}
        </div>

        <Button variant="ghost" size="sm" onClick={onToggleAll} className="h-6 text-[11px]">
          {isCollapsed ? (
            <>
              <ChevronDown className="mr-1 h-3 w-3" />
              Expand All
            </>
          ) : (
            <>
              <ChevronUp className="mr-1 h-3 w-3" />
              Collapse All
            </>
          )}
        </Button>
      </div>

      {/* Description */}
      <p className="text-[11px] text-muted-foreground">
        {helpText}
      </p>

      {/* Children */}
      {children}
    </div>
  );
}

interface UnifiedParametersGroupProps {
  parameters: Record<string, { info: ParameterInfo; override?: AdminParameterOverride; group: "input" | "output" | "settings" }>;
  mainOutputParam?: string | null;
  savedOrder?: {
    input?: string[];
    output?: string[];
    settings?: string[];
  };
  onParametersChange: (updates: Record<string, AdminParameterOverride | string[]>) => void;
  onGroupChange: (paramName: string, newGroup: "input" | "output" | "settings") => void;
}

export function UnifiedParametersGroup({
  parameters,
  mainOutputParam,
  savedOrder,
  onParametersChange,
  onGroupChange,
}: UnifiedParametersGroupProps) {
  const [expandedParams, setExpandedParams] = React.useState<Set<string>>(new Set());
  const [collapsedGroups, setCollapsedGroups] = React.useState({
    input: true,
    output: true,
    settings: true,
  });
  const [activeId, setActiveId] = React.useState<string | null>(null);
  const [activeDropZone, setActiveDropZone] = React.useState<string | null>(null);

  // Group parameters with order state
  const [groupOrders, setGroupOrders] = React.useState<{
    input: string[];
    output: string[];
    settings: string[];
  }>({ input: [], output: [], settings: [] });

  // Initialize and update group orders when parameters change
  React.useEffect(() => {
    const groups = {
      input: [] as string[],
      output: [] as string[],
      settings: [] as string[],
    };

    // Collect all params by group
    for (const [paramName, param] of Object.entries(parameters)) {
      groups[param.group].push(paramName);
    }

    // Apply saved order if available
    if (savedOrder) {
      // Sort each group based on saved order
      (["input", "output", "settings"] as const).forEach((group) => {
        if (savedOrder[group] && savedOrder[group]!.length > 0) {
          const saved = savedOrder[group]!;
          // Put saved items first in saved order, then append any new items
          const ordered = saved.filter((name) => groups[group].includes(name));
          const newItems = groups[group].filter((name) => !saved.includes(name));
          groups[group] = [...ordered, ...newItems];
        }
      });
    }

    setGroupOrders(groups);
  }, [parameters, savedOrder]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;

    if (!over) {
      setActiveDropZone(null);
      return;
    }

    const activeParamName = active.id as string;
    const activeParam = parameters[activeParamName];
    const activeGroup = activeParam?.group;

    const targetDropZone = over.data.current?.group as string | undefined;

    if (targetDropZone) {
      if (targetDropZone !== activeGroup) {
        setActiveDropZone(targetDropZone);
      } else {
        setActiveDropZone(null);
      }
    } else {
      const overParamName = over.id as string;
      const overParam = parameters[overParamName];
      if (overParam && overParam.group !== activeGroup) {
        setActiveDropZone(overParam.group);
      } else {
        setActiveDropZone(null);
      }
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    setActiveDropZone(null);

    if (!over || active.id === over.id) return;

    const activeParamName = active.id as string;
    const overParamName = over.id as string;
    const activeGroup = parameters[activeParamName]?.group;
    const overGroup = parameters[overParamName]?.group;

    // Check if dropped on a droppable zone (group container)
    const targetDropZone = over.data.current?.group as "input" | "output" | "settings" | undefined;

    if (targetDropZone && targetDropZone !== activeGroup) {
      // Moved to different group
      onGroupChange(activeParamName, targetDropZone);
    } else if (overGroup && activeGroup === overGroup) {
      // Same group reordering
      const group = activeGroup;
      const oldIndex = groupOrders[group].indexOf(activeParamName);
      const newIndex = groupOrders[group].indexOf(overParamName);
      const newOrder = arrayMove(groupOrders[group], oldIndex, newIndex);

      // Update local state
      setGroupOrders((prev) => ({
        ...prev,
        [group]: newOrder,
      }));

      // Save the new order to parent (outside setState callback)
      const orderKey = `__${group}Order` as const;
      onParametersChange({ [orderKey]: newOrder });
    } else if (targetDropZone) {
      // Dropped on empty group zone
      onGroupChange(activeParamName, targetDropZone);
    }
  };

  const handleToggle = (paramName: string) => {
    setExpandedParams((prev) => {
      const next = new Set(prev);
      if (next.has(paramName)) {
        next.delete(paramName);
      } else {
        next.add(paramName);
      }
      return next;
    });
  };

  const handleToggleGroup = (group: "input" | "output" | "settings") => {
    setCollapsedGroups((prev) => ({ ...prev, [group]: !prev[group] }));

    if (collapsedGroups[group]) {
      // Expanding - expand all params in group
      const newExpanded = new Set(expandedParams);
      groupOrders[group].forEach(p => newExpanded.add(p));
      setExpandedParams(newExpanded);
    } else {
      // Collapsing - collapse all params in group
      const newExpanded = new Set(expandedParams);
      groupOrders[group].forEach(p => newExpanded.delete(p));
      setExpandedParams(newExpanded);
    }
  };

  const handleOverrideChange = (paramName: string, group: "input" | "output" | "settings", override: AdminParameterOverride) => {
    const updates: Record<string, AdminParameterOverride> = {};

    if (group === "input") {
      updates[paramName] = { ...override, group: "input", isInput: true };
    } else if (group === "output") {
      updates[paramName] = { ...override, group: "output", isOutput: true };
    } else {
      updates[paramName] = { ...override, group: "settings" };
    }

    onParametersChange(updates);
  };

  const handleMainOutputChange = (paramName: string, isMain: boolean) => {
    if (isMain) {
      const updates: Record<string, AdminParameterOverride> = {};

      // Clear isMainOutput from all other params
      for (const [name, param] of Object.entries(parameters)) {
        if (param.group === "output" && name !== paramName) {
          updates[name] = { ...param.override, isMainOutput: false };
        }
      }

      // Set this param as main output
      updates[paramName] = { ...parameters[paramName].override, isMainOutput: true };

      onParametersChange(updates);
    } else {
      onParametersChange({ [paramName]: { ...parameters[paramName].override, isMainOutput: false } });
    }
  };

  const anyExpanded = (group: "input" | "output" | "settings") => {
    return groupOrders[group].some(p => expandedParams.has(p));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="space-y-6">
        {/* Input Handles Group */}
        <DroppableGroup
          id="input"
          title="Input Handles"
          color="bg-blue-500"
          count={groupOrders.input.length}
          isCollapsed={collapsedGroups.input}
          onToggleAll={() => handleToggleGroup("input")}
          helpText={
            anyExpanded("input")
              ? "Collapse parameters to enable drag & drop reordering"
              : "Drag to reorder input handles or move to other groups"
          }
          isActiveDropZone={activeDropZone === "input"}
        >
          {groupOrders.input.length === 0 ? (
            <div className="rounded-lg border border-dashed p-6 text-center">
              <p className="text-xs text-muted-foreground">No input handles configured</p>
              <p className="text-[11px] text-muted-foreground mt-1">
                Drag parameters here to create input handles
              </p>
            </div>
          ) : (
            <SortableContext items={groupOrders.input} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {groupOrders.input.map((paramName) => {
                  const param = parameters[paramName];
                  return (
                    <SortableParameter
                      key={paramName}
                      paramName={paramName}
                      paramInfo={param.info}
                      override={param.override}
                      group="input"
                      isMainOutput={false}
                      isExpanded={expandedParams.has(paramName)}
                      onToggle={() => handleToggle(paramName)}
                      onChange={(override) => handleOverrideChange(paramName, "input", override)}
                    />
                  );
                })}
              </div>
            </SortableContext>
          )}
        </DroppableGroup>

        {/* Output Params Group */}
        <DroppableGroup
          id="output"
          title="Output Params"
          color="bg-green-500"
          count={groupOrders.output.length}
          mainOutputParam={mainOutputParam}
          isCollapsed={collapsedGroups.output}
          onToggleAll={() => handleToggleGroup("output")}
          helpText={
            anyExpanded("output")
              ? "Collapse parameters to enable drag & drop reordering"
              : "Drag to reorder outputs or move to other groups • Mark one as main output"
          }
          isActiveDropZone={activeDropZone === "output"}
        >
          {groupOrders.output.length === 0 ? (
            <div className="rounded-lg border border-dashed p-6 text-center">
              <p className="text-xs text-muted-foreground">No output parameters configured</p>
              <p className="text-[11px] text-muted-foreground mt-1">
                Drag parameters here to create output params
              </p>
            </div>
          ) : (
            <SortableContext items={groupOrders.output} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {groupOrders.output.map((paramName) => {
                  const param = parameters[paramName];
                  return (
                    <SortableParameter
                      key={paramName}
                      paramName={paramName}
                      paramInfo={param.info}
                      override={param.override}
                      group="output"
                      isMainOutput={mainOutputParam === paramName}
                      isExpanded={expandedParams.has(paramName)}
                      onToggle={() => handleToggle(paramName)}
                      onChange={(override) => handleOverrideChange(paramName, "output", override)}
                      onMainOutputChange={(isMain) => handleMainOutputChange(paramName, isMain)}
                    />
                  );
                })}
              </div>
            </SortableContext>
          )}
        </DroppableGroup>

        {/* Node Settings Group */}
        <DroppableGroup
          id="settings"
          title="Node Settings"
          color="bg-purple-500"
          count={groupOrders.settings.length}
          isCollapsed={collapsedGroups.settings}
          onToggleAll={() => handleToggleGroup("settings")}
          helpText={
            anyExpanded("settings")
              ? "Collapse parameters to enable drag & drop reordering"
              : "Drag to reorder settings or move to other groups"
          }
          isActiveDropZone={activeDropZone === "settings"}
        >
          {groupOrders.settings.length === 0 ? (
            <div className="rounded-lg border border-dashed p-6 text-center">
              <p className="text-xs text-muted-foreground">No node settings configured</p>
              <p className="text-[11px] text-muted-foreground mt-1">
                Parameters not marked as input/output will appear here
              </p>
            </div>
          ) : (
            <SortableContext items={groupOrders.settings} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {groupOrders.settings.map((paramName) => {
                  const param = parameters[paramName];
                  return (
                    <SortableParameter
                      key={paramName}
                      paramName={paramName}
                      paramInfo={param.info}
                      override={param.override}
                      group="settings"
                      isMainOutput={false}
                      isExpanded={expandedParams.has(paramName)}
                      onToggle={() => handleToggle(paramName)}
                      onChange={(override) => handleOverrideChange(paramName, "settings", override)}
                    />
                  );
                })}
              </div>
            </SortableContext>
          )}
        </DroppableGroup>
      </div>

      {/* Drag Overlay */}
      <DragOverlay>
        {activeId && parameters[activeId] ? (
          <div className="rounded-lg border bg-card p-2 shadow-lg opacity-90">
            <code className="text-xs font-mono font-semibold">{activeId}</code>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
